/**
 * Générateur — Il vérifie la cohérence des résultats du point de vue des
 * unités pour des grandeurs simples ou composées.
 * Attendu ID : 235 | Niveau : 3ème | Domaine : Grandeurs (d18, O5)
 *
 * Source doc prof (CS2 + Créé n°13). Plan auto-validé 2026-07-17 (TODO §6) :
 * « ordonner » (vitesses en unités mélangées, valeurs m/s DISTINCTES par
 * construction), « pompes » (quel débit est le plus grand ? QCM), « unites »
 * (relier grandeur ↔ unité — le Créé n°13a), « vf » (cohérences V/F, dont
 * la ligne numérique x,5 h calculée).
 */

import { randomChoice, randInt, shuffle, makeQcm, makeTexteTrou, makeRelier, makeVraiFaux, makeOrdonner } from '../../../utils';

const ATTENDU_ID = 235;
const L_ORDONNER = 'Ranger des vitesses en unités mélangées';
const L_POMPES = 'Comparer deux débits d\'unités différentes';
const L_UNITES = 'Associer chaque grandeur à son unité';
const L_VF = 'Cohérence des unités : vrai ou faux';
const opts = (label) => ({ niveau: '3eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const frNb = (x) => String(Math.round(x * 1000) / 1000).replace('.', ',');

// ═════════ « ordonner » — vitesses mélangées ═════════

const genOrdonnerVitesses = () => {
    // 4 vitesses à valeurs m/s ENTIÈRES et DISTINCTES, exprimées en 4 unités.
    let ms;
    for (let essai = 0; essai < 60; essai++) {
        ms = shuffle([5, 8, 10, 12, 15, 20, 25, 30]).slice(0, 4);
        break;
    }
    const [a, b, c, d] = ms;
    const items = [
        { v: a, txt: `${frNb(a * 3.6)} km/h` },
        { v: b, txt: `${b} m/s` },
        { v: c, txt: `${c * 60} m/min` },
        { v: d, txt: `${d * 100} cm/s` },
    ];
    const ordres = [...items].sort((u, w) => u.v - w.v);
    const payload = makeOrdonner(ordres.map(i => i.txt),
        // « Convertis tout en m/s » livrait la stratégie de comparaison.
        '<p><b>Range ces vitesses de la plus lente à la plus rapide.</b></p>',
        opts(L_ORDONNER));
    payload._meta.params = { ms };
    return payload;
};

// ═════════ « pompes » — QCM ═════════

const genPompesQcm = () => {
    // A en L/s, B en m³/h : comparer via L/h. Valeurs DISTINCTES par construction.
    let aLs; let bM3h;
    for (let essai = 0; essai < 60; essai++) {
        aLs = randomChoice([0.15, 0.2, 0.25, 0.5, 0.75, 1, 1.5, 2]);
        bM3h = randomChoice([0.5, 0.9, 1.8, 2.7, 3.6, 5.4, 7.2]);
        if (!((aLs * 3600) === bM3h * 1000)) break;        // jamais égales
        aLs = null;
    }
    if (aLs === null) { aLs = 0.2; bM3h = 7.2; }
    const aM3h = (aLs * 3600) / 1000;
    const correct = aM3h > bM3h ? 'La pompe A' : 'La pompe B';
    const payload = makeQcm(['La pompe A', 'La pompe B', 'Elles ont le même débit'], correct,
        `<p>La pompe A débite ${frNb(aLs)} L/s ; la pompe B débite ${frNb(bM3h)} m³/h.</p>`
        // « Mets les deux dans la même unité » était la démarche à trouver ;
        // l'équivalence 1 L/s = 3,6 m³/h reste, c'est une donnée.
        + '<p>Rappel : 1 L/s = 3,6 m³/h.</p>'
        + '<p><b>Laquelle a le plus grand débit ?</b></p>',
        opts(L_POMPES));
    payload._meta.params = { aLs, bM3h, aM3h };
    return payload;
};

// ═════════ « unites » — relier grandeur ↔ unité ═════════

const GRANDEURS = [
    ['une durée', 'h, min, s'],
    ['une aire', 'm²'],
    ['un volume', 'm³'],
    ['une vitesse', 'km/h ou m/s'],
    ['un débit', 'm³/s ou L/h'],
    ['une masse volumique', 'kg/m³'],
    ['une masse', 'kg ou g'],
    ['une énergie', 'kWh ou J'],
];

const genUnitesRelier = () => {
    const tirage = shuffle(GRANDEURS).slice(0, 4);
    const payload = makeRelier(tirage.map(([g, u]) => ({ source: g, target: u })),
        '<p><b>Associe chaque grandeur à son unité.</b></p>', opts(L_UNITES));
    payload._meta.params = { tirage };
    return payload;
};

// ═════════ « vf » — cohérences ═════════

const genVfTableau = () => {
    const lignes = []; const bonnes = [];
    // 1. aire en m³ ?
    if (randInt(0, 1)) { lignes.push('Une aire peut s\'exprimer en m³.'); bonnes.push(1); }
    else { lignes.push('Une aire peut s\'exprimer en cm².'); bonnes.push(0); }
    // 2. définition du débit
    if (randInt(0, 1)) { lignes.push('Un débit est un volume divisé par une durée.'); bonnes.push(0); }
    else { lignes.push('Un débit est une durée divisée par un volume.'); bonnes.push(1); }
    // 3. ligne NUMÉRIQUE calculée : h,5 = h h 30 min (V) ou h h 50 min (F)
    {
        const h = randInt(1, 6);
        if (randInt(0, 1)) { lignes.push(`${h},5 h = ${h} h 30 min.`); bonnes.push(0); }
        else { lignes.push(`${h},5 h = ${h} h 50 min.`); bonnes.push(1); }
    }
    // 4. volume ↔ litres
    {
        const n = randomChoice([1, 2, 5]);
        if (randInt(0, 1)) { lignes.push(`${n} m³ = ${n * 1000} L.`); bonnes.push(0); }
        else { lignes.push(`${n} m³ = ${n * 100} L.`); bonnes.push(1); }
    }
    if (!bonnes.includes(0)) { lignes[3] = '1 m³ = 1000 L.'; bonnes[3] = 0; }
    if (!bonnes.includes(1)) { lignes[3] = '1 m³ = 100 L.'; bonnes[3] = 1; }
    const ordre = shuffle(lignes.map((_, i) => i));
    const payload = makeVraiFaux(ordre.map(i => lignes[i]), ordre.map(i => bonnes[i]),
        '<p><b>Vrai ou faux ?</b></p>', opts(L_VF));
    payload._meta.params = { lignes, bonnes };
    return payload;
};

const GENERATORS = {
    ordonner_ordonner: genOrdonnerVitesses,
    pompes_qcm_unique: genPompesQcm,
    unites_relier: genUnitesRelier,
    vf_tableau_vf: genVfTableau,
};
const FORMAT_LABELS = { ordonner: 'Ordonner', qcm_unique: 'QCM', relier: 'Relier', tableau_vf: 'Vrai/Faux' };
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));
export const CATEGORIES = [
    { id: 'ordonner', label: L_ORDONNER, formatsList: buildFormats('ordonner') },
    { id: 'pompes', label: L_POMPES, formatsList: buildFormats('pompes') },
    { id: 'unites', label: L_UNITES, formatsList: buildFormats('unites') },
    { id: 'vf', label: L_VF, formatsList: buildFormats('vf') },
];
export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
